/**
 * CSV Export Utility - Client Requirements
 *
 * Utility functions for exporting data to CSV format
 */

/**
 * Convert array of objects to CSV string
 */
export const convertToCSV = <T extends Record<string, any>>(
  data: T[],
  columns: { key: keyof T; header: string }[]
): string => {
  if (data.length === 0) {
    return ''
  }

  // Create header row
  const headers = columns.map(col => escapeCSVValue(col.header))
  const headerRow = headers.join(',')

  // Create data rows
  const dataRows = data.map(row => {
    const values = columns.map(col => {
      const value = row[col.key]
      return escapeCSVValue(formatValue(value))
    })
    return values.join(',')
  })

  // Combine header and data rows
  return [headerRow, ...dataRows].join('\n')
}

/**
 * Escape CSV value (handle commas, quotes, newlines)
 */
const escapeCSVValue = (value: string): string => {
  if (value == null) {
    return ''
  }

  const stringValue = String(value)

  // If value contains comma, quote, or newline, wrap in quotes and escape quotes
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`
  }

  return stringValue
}

/**
 * Format value for CSV output
 */
const formatValue = (value: any): string => {
  if (value == null) {
    return ''
  }

  // Handle dates
  if (value instanceof Date) {
    return value.toISOString().split('T')[0] || '' // YYYY-MM-DD
  }

  // Handle objects (convert to JSON string)
  if (typeof value === 'object') {
    return JSON.stringify(value)
  }

  // Handle booleans
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }

  // Handle numbers
  if (typeof value === 'number') {
    return value.toString()
  }

  return String(value)
}

/**
 * Download CSV file
 */
export const downloadCSV = (csvContent: string, filename: string): void => {
  // Create blob
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })

  // Create download link
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)

  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  // Clean up
  URL.revokeObjectURL(url)
}

/**
 * Export data to CSV file
 */
export const exportToCSV = <T extends Record<string, any>>(
  data: T[],
  columns: { key: keyof T; header: string }[],
  filename: string
): void => {
  const csvContent = convertToCSV(data, columns)
  downloadCSV(csvContent, filename)
}

/**
 * Generate filename with timestamp
 */
export const generateCSVFilename = (baseName: string): string => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  const hours = String(now.getHours()).padStart(2, '0')
  const minutes = String(now.getMinutes()).padStart(2, '0')

  return `${baseName}_${year}${month}${day}_${hours}${minutes}.csv`
}

/**
 * Format currency for CSV export (GBP)
 */
export const formatCurrencyForCSV = (value: number): string => {
  return `£${value.toFixed(2)}`
}

/**
 * Format date for CSV export
 */
export const formatDateForCSV = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  return `${day}/${month}/${year}`
}

/**
 * Format datetime for CSV export
 */
export const formatDateTimeForCSV = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')
  return `${day}/${month}/${year} ${hours}:${minutes}`
}

/**
 * Calculate days ago for CSV export
 */
export const formatDaysAgoForCSV = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date
  const now = new Date()
  const diffTime = Math.abs(now.getTime() - d.getTime())
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return '1 day ago'
  return `${diffDays} days ago`
}

export default {
  convertToCSV,
  downloadCSV,
  exportToCSV,
  generateCSVFilename,
  formatCurrencyForCSV,
  formatDateForCSV,
  formatDateTimeForCSV,
  formatDaysAgoForCSV,
}
